import { FLAGS } from '@gozoki/feature-flags'
import { Button, CircularProgress, Typography } from '@mui/material'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useContext } from 'react'
import { allNavLinks } from '~/components/new/navigation/nav-links'
import { useNavItemVisibility } from '~/components/new/navigation/useNavItemVisible'
import { useFeatureFlag, useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import { PROJECT_TRACKING_READ } from '~/lib/project-tracking'
import { useMigratedRight, useRbacV2Enabled } from '~/utils/rbac'
import { AuthContext } from '../utils/context/auth.context'

export const Route = createFileRoute('/')({
  component: IndexComponentPage,
})

function IndexComponentPage() {
  const { authenticated, isLoading, hasRights, logout } = useContext(AuthContext)
  const v2Enabled = useRbacV2Enabled()
  const isNavItemVisible = useNavItemVisibility()
  // `/project-tracking` est hors navbar : ni le menu (flag ON) ni la chaîne de droits (flag OFF) ne
  // l'atteignent, il faut le tester explicitement dans les deux régimes.
  // Le flag Flipt entre dans la condition, sinon la racine renverrait vers une page qui, flag OFF,
  // renvoie vers la racine — boucle infinie pour un compte dont c'est le seul droit.
  const projectTrackingEnabled = useFeatureFlag(FLAGS.projectTracking)
  const flagsReady = useFeatureFlagsReady()
  const canOpenProjectTracking =
    useMigratedRight(PROJECT_TRACKING_READ, ['PROJECT_TRACKING']) && projectTrackingEnabled

  // La visibilité des entrées du menu dépend de leurs flags : tant que Flipt s'initialise, une
  // entrée flaggée est invisible et on atterrirait sur la mauvaise page (voire sur « Aucune page
  // accessible » pour un admin dont toutes les entrées sont flaggées). Même raison pour le suivi
  // projet : un compte client dont c'est le seul droit atterrirait sur `/network-dashboard`.
  if (isLoading || !flagsReady) {
    return <CircularProgress />
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />
  }

  // Flag ON : la seule source de vérité est ce que l'admin peut réellement ouvrir. On atterrit sur
  // la première entrée visible du menu, dans l'ordre où le menu les affiche.
  if (v2Enabled) {
    const firstVisible = allNavLinks.find(isNavItemVisible)

    if (!firstVisible) {
      if (canOpenProjectTracking) {
        return <Navigate to="/project-tracking" replace />
      }

      return (
        <main className="flex min-h-screen items-center justify-center bg-sidebar px-6 py-10">
          <section className="w-full max-w-xl rounded-3xl border border-sidebar-border bg-background p-8 text-center shadow-xs">
            <Typography variant="h4" className="mb-3! font-semibold">
              Aucune page accessible
            </Typography>
            <Typography className="mb-6! text-muted-foreground">
              Votre compte n'a aucune permission permettant d'ouvrir une page du back-office.
              Rapprochez-vous d'un administrateur pour vous faire attribuer un rôle.
            </Typography>

            {/* Seule sortie : sans page accessible, toute navigation ramène ici. */}
            <Button variant="contained" onClick={logout}>
              Déconnexion
            </Button>
          </section>
        </main>
      )
    }

    return <Navigate {...firstVisible.linkOptions} replace />
  }

  if (hasRights(['READER'])) {
    return <Navigate to="/network-dashboard" replace />
  }
  if (hasRights(['STOCK_READER'])) {
    return <Navigate to="/stores" replace />
  }
  if (hasRights(['ACCOUNTING_READER'])) {
    return <Navigate to="/accounting" replace />
  }
  if (canOpenProjectTracking) {
    return <Navigate to="/project-tracking" replace />
  }
  return <Navigate to="/network-dashboard" replace />
}
